import { ReportControlField, Cooperative } from 'models';
import { memo, useMemo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { orderOptions, sortOptions, publicOptions } from 'configs/reports';

import { HTMLParser, PageTitle } from 'containers';
import { Select, FiltersBar, Download, ControlWrapper } from 'components';

import clsx from 'clsx';
import { useStyles } from './style';

type Entity =
  | 'vote-register'
  | 'share-register'
  | 'contact-information'
  | 'maintenance/contact-information';

function getOptions(entity: Entity) {
  switch (entity) {
    case 'vote-register':
      return {
        sort: sortOptions,
        order: orderOptions,
      };
    case 'share-register':
      return {
        sort: sortOptions,
        order: orderOptions,
        pub: publicOptions,
      };
    case 'contact-information':
      return null;
    default:
      return null;
  }
}

interface Props {
  cooperative: Cooperative | null;
  entity: Entity;
}

interface State {
  sort: ReportControlField;
  order: ReportControlField;
  pub: ReportControlField;
}

interface TranslatedOptions {
  sort: ReportControlField[];
  order: ReportControlField[];
  pub?: ReportControlField[];
}

function buildURL(state: State, entity: Entity, coopId: string | undefined) {
  const q = new URLSearchParams();

  if (entity === 'vote-register') {
    q.set('fieldForSort', `${state.sort.value}`);
    q.set('orderBy', `${state.order.value}`);
  } else if (entity === 'share-register') {
    q.set('fieldForSort', `${state.sort.value}`);
    q.set('orderBy', `${state.order.value}`);
    q.set('isPublic', `${state.pub?.value}`);
  } else if (entity === 'maintenance/contact-information') {
    entity = 'contact-information';
  }

  return `/reports/cooperative/${entity}/${coopId}?${q.toString()}`;
}

const ReportViewer: React.FC<Props> = ({ cooperative, entity }) => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const xs = useMediaQuery(theme.breakpoints.down(theme.size.xs));
  const classes = useStyles();
  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },
      select: classes.selectRoot,
      /* eslint-disable-next-line */
    }),
    []
  );

  const [state, setState] = useState<State>({
    sort: {
      label: t('#portal.report.bar.option.label.osakkeet'),
      value: 2,
    },
    order: {
      label: t('#portal.report.bar.option.label.nouseva'),
      value: 1,
    },
    pub: {
      label: t('#portal.report.bar.option.label.no'),
      value: 'false',
    },
  });

  const url = useMemo(
    () => buildURL(state, entity, cooperative?.Id),
    [state, entity, cooperative?.Id]
  );

  const translatedOptions = useMemo(() => {
    const options = getOptions(entity);

    if (!options) {
      return null;
    }

    return Object.keys(options).reduce((acc, next) => {
      const optionsList = options[next];

      if (!optionsList) {
        return acc;
      }

      acc[next] = optionsList.map((option) => ({
        ...option,
        label: t(option.label),
      }));

      return acc;
    }, {} as TranslatedOptions);

    /* eslint-disable-next-line */
  }, [entity]);

  const handleChangeField = useCallback(
    (field: ReportControlField, name?: string) => {
      if (name) {
        setState((prevState) => ({
          ...prevState,
          [name]: field,
        }));
      }
    },
    []
  );

  return (
    <div className={classes.root} ref={ref}>
      <PageTitle />
      <FiltersBar className={classes.filtersBar}>
        <div className={classes.barWrapper}>
          {entity !== 'contact-information' && translatedOptions ? (
            <ControlWrapper className={classes.controlWrapper}>
              <span className={classes.label}>
                {t('#portal.report.bar.label.fieldForSort1')}:
              </span>
              <Select
                value={state.sort}
                options={translatedOptions.sort}
                onSelect={handleChangeField}
                name="sort"
                labelField={'label'}
                valueField={'value'}
                withButton={true}
                cls={selectCls}
                parentWidth={xs}
              />
            </ControlWrapper>
          ) : null}
          {entity !== 'contact-information' && translatedOptions ? (
            <ControlWrapper className={classes.controlWrapper}>
              <span className={classes.label}>
                {t('#portal.report.bar.label.fieldForSort2')}:
              </span>
              <Select
                value={state.order}
                options={translatedOptions.order}
                onSelect={handleChangeField}
                name="order"
                labelField={'label'}
                valueField={'value'}
                withButton={true}
                cls={selectCls}
                parentWidth={xs}
              />
            </ControlWrapper>
          ) : null}
          {entity !== 'contact-information' &&
          translatedOptions &&
          translatedOptions.pub ? (
            <ControlWrapper className={classes.controlWrapper}>
              <span className={classes.label}>
                {t('#portal.report.share-register.filter.ispublic')}
              </span>
              <Select
                value={state.pub}
                options={translatedOptions.pub}
                onSelect={handleChangeField}
                name="pub"
                labelField={'label'}
                valueField={'value'}
                withButton={true}
                cls={selectCls}
                parentWidth={xs}
              />
            </ControlWrapper>
          ) : null}
          <div className={classes.flex}>
            <ControlWrapper>
              <Download
                url={`${url}&download=true&downloadFormat=3`}
                name={`${entity}__${cooperative?.Name}`}
                extension="pdf"
              />
            </ControlWrapper>
            <ControlWrapper
              className={clsx({
                [classes.maintenanceContactInformation]:
                  entity === 'maintenance/contact-information',
              })}
            >
              <Download
                url={`${url}&download=true&downloadFormat=8`}
                name={`${entity}__${cooperative?.Name}`}
                extension="doc"
              />
            </ControlWrapper>
            <ControlWrapper
              className={clsx({
                [classes.maintenanceContactInformation]:
                  entity === 'maintenance/contact-information',
              })}
            >
              <Download
                url={`${url}&download=true&downloadFormat=7`}
                name={`${entity}__${cooperative?.Name}`}
                extension="xls"
              />
            </ControlWrapper>
          </div>
        </div>
      </FiltersBar>
      <HTMLParser url={url} />
    </div>
  );
};

export default memo(ReportViewer);
